// Shared configuration for AI processing styles
// Used across ProcessingOptions, Settings, AIStylesSection, and backend prompts
// This is the SINGLE SOURCE OF TRUTH for all AI style information

// ============================================================================
// ARCHITECTURE NOTES
// ============================================================================
// This file serves as the definitive source for all AI style definitions.
// It consolidates BOTH:
//   1. Marketing/UI Information (titles, descriptions, colors, examples)
//   2. AI Prompt Templates (used by the backend for metadata generation)
//
// FRONTEND USAGE:
//   - Imported by ProcessingOptions.js, Settings.js, AIStylesSection.js
//   - Uses marketing info + helper functions (getStyle, getStylesByCategory)
//
// BACKEND USAGE:
//   - Imported by server/services/aiService.js
//   - Uses getPromptTemplate() function and style.getPrompt() methods
//   - Ensures backend prompts match the exact styles displayed to users
//
// MAINTENANCE:
//   - Update a style here and changes propagate everywhere automatically
//   - No need to sync prompts between multiple files
//   - All stakeholders (users seeing landing page, selection screens) get
//     consistent information about each style
// ============================================================================

// Helper to generate sentence structure guidance
const generateSentenceStructure = (sentenceCount, structures) => {
  if (sentenceCount === 1) {
    return `Single sentence combining: ${structures.join(' + ')}.`;
  } else if (sentenceCount === 2) {
    return `Sentence 1: ${structures[0]}. Sentence 2: ${structures[1] || structures[0]}.`;
  } else {
    const result = [];
    for (let i = 0; i < sentenceCount && i < structures.length; i++) {
      result.push(`Sentence ${i + 1}: ${structures[i]}`);
    }
    if (sentenceCount > structures.length) {
      result.push(`Additional sentences: expand on the theme while maintaining consistency`);
    }
    return result.join(' ') + '.';
  }
};

// Lexicon definitions for style-specific guidance
const LEXICONS = {
  artisticMood: [
    "quiet", "moody", "stark", "hushed", "luminous", "hazy",
    "crisp", "harsh", "soft", "grainy", "brooding", "warm",
    "cool", "muted", "vivid", "nocturnal", "backlit", "golden-hour"
  ],
  descriptiveAdj: [
    "overcast", "backlit", "low-tide", "street-level", "long-exposure",
    "wide-angle", "close-up", "handheld", "panoramic", "rain-soaked",
    "foggy", "snow-dusted", "neon-lit", "shadowed"
  ],
  fancifulFlavor: [
    "cheeky", "offbeat", "quirky", "tongue-in-cheek", "playful"
  ]
};

// Base prompt that applies to all styles
const BASE_PROMPT = `Analyze this photo carefully and generate metadata that feels written by an attentive human editor.
Output strictly as the following format:
Title: [A concise, engaging title, hard cap 60 characters; prefer 35–55; one clause; no emojis; no trailing punctuation.]
Description: [A brief description. Follow the style-specific sentence structure guidance below.]
Tags: [Comma-separated keywords, relevant and specific. 10-15 items, lowercase; no punctuation or emojis; no duplicates; include subject(s), location, time/season if known, and visual attributes (e.g., long-exposure, backlit)]

General rules that always apply:
- Do not invent facts or places. If info is missing, omit it.
- Do not mention missing information. Do not use phrases like "location unknown" or "time not known"
- Avoid meta phrases like "photo of", "image of", "This picture shows", "This picture is a", "in this image"
- Modern, concrete vocabulary. Avoid clichés and superlatives.
- DO NOT USE THESE WORDS/PHRASES: serenity, serene, tranquil, tranquility, whisper, whispers, enchanting, ethereal, timeless, majestic, breathtaking, sublime, stunning, magical, fairytale, dreamy, awe-inspiring.
- Make the writing feel distinct from other styles; vary rhythm, diction, and structure aggressively.
- NEVER include years or dates in titles or descriptions. Years are frequently incorrect and should always be omitted.
- NEVER use em dashes (—) in titles or descriptions. Use commas, periods, or simple word breaks instead.
- Be extremely careful with specific city/location names. Only mention a specific city if you are highly confident based on distinctive landmarks. When uncertain, use broader terms.
- Time-of-day assessment: Be cautious with lighting direction. Morning light comes from the east, dusk from the west. If uncertain, use neutral terms like "daylight".`;

export const AI_STYLES = {
  descriptive: {
    key: 'descriptive',
    title: 'Factual & Straightforward',
    shortTitle: 'Descriptive',
    description: 'Clear, accurate descriptions',
    longDescription: 'Clear, factual descriptions that accurately capture what\'s visible. Perfect for searchability and professional use.',
    bestFor: 'Documentation, real estate, travel logs',
    example: 'A golden retriever sits on green grass in a sunny park.',
    exampleTitle: 'San Francisco City View from Fire Escape',
    exampleDescription: 'Urban landscape showing San Francisco\'s downtown skyline including the Transamerica Pyramid, photographed from a building\'s fire escape balcony on a partly cloudy day.',
    gradient: 'from-blue-50 to-blue-100',
    selectedGradient: 'from-blue-500 to-blue-600',
    borderColor: 'border-blue-200',
    selectedBorderColor: 'border-blue-500',
    iconColor: 'text-blue-600',
    category: 'Professional',
    icon: 'FileText',
    emoji: '📝',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Descriptive (factual, succinct, search-friendly)
- Purpose: help viewers instantly know subject, place, and one visual technique.
- Voice: neutral, documentary; no figurative language or emotion words.
- Title: ≤ 55 chars. Use pattern: <main subject>, <specific detail> (<location if known>).
- Description: ${generateSentenceStructure(sentenceCount, ['subject + location/context', 'single, concrete visual attribute (e.g., framing, light, weather) using lexicon terms such as ' + LEXICONS.descriptiveAdj.join(", ")])}
- Use one crisp sentence per detail; never string clauses with "and" more than once.
- Never include camera gear, settings, or personal opinions unless explicitly provided.`
  },
  
  artistic: {
    key: 'artistic',
    title: 'Artistic Minimal',
    shortTitle: 'Artistic Minimal',
    description: 'Sparse, calm, and meditative',
    longDescription: 'Sparse, minimal descriptions that feel like a pause or held breath. Captures stillness and emotion through simplicity and quietness.',
    bestFor: 'Abstract imagery, minimalist compositions, meditative work',
    example: 'Soft, diffused light across an empty surface.',
    exampleTitle: 'Soft Divide',
    exampleDescription: 'Quiet geometry in shadow. Stillness that speaks.',
    gradient: 'from-purple-50 to-purple-100',
    selectedGradient: 'from-purple-500 to-purple-600',
    borderColor: 'border-purple-200',
    selectedBorderColor: 'border-purple-500',
    iconColor: 'text-purple-600',
    category: 'Creative',
    popular: true,
    icon: 'Palette',
    emoji: '🎨',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Artistic Minimal (Sparse, calm, meditative)
- Purpose: Create minimal, evocative descriptions that feel like a pause or held breath. Capture stillness and emotion through simplicity and quietness.
- Voice: Sparse, calm, meditative. The fewest words possible—feels like silence rather than speech.
- Title Guidelines (1–3 words, Title Case):
  * Avoid punctuation and unnecessary detail
  * Capture stillness, texture, or subtle contrast
  * Express emotion through simplicity and quietness
  * Avoid literal descriptions, locations, or clichés
  * Feel like a fragment of silence, not a sentence
  * Example titles: "Soft Divide", "Under Glass", "Afterlight", "Quiet Geometry", "Unfinished Sky"
- Description: ${generateSentenceStructure(sentenceCount, ['minimal observation using sparse, restrained language', 'quietness or stillness captured through texture, light, or negative space'])}
- Tags: Use minimal, mood-focused tags ("stillness", "silence", "texture", "light", "shadow") prioritizing emotional/atmospheric words.
- Embrace emptiness. Use fewer words than feels necessary.`
  },
  
  fanciful: {
    key: 'fanciful',
    title: 'Playful & Social',
    shortTitle: 'Fanciful',
    description: 'Imaginative and fun',
    longDescription: 'Creative, imaginative descriptions that tell a story. Adds personality and charm to your photos.',
    bestFor: 'Personal collections, storytelling, social sharing',
    example: 'Captain Barksworth surveys his grassy kingdom, plotting his next great adventure!',
    exampleTitle: 'Climbing to the Clouds: A Secret City Observatory',
    exampleDescription: 'From this secret metal observatory, high above the bustling world below, the city becomes a living storybook. Each building holds a thousand tales while fluffy cloud-ships sail across the endless blue.',
    gradient: 'from-green-50 to-green-100',
    selectedGradient: 'from-green-500 to-green-600',
    borderColor: 'border-green-200',
    selectedBorderColor: 'border-green-500',
    iconColor: 'text-green-600',
    category: 'Creative',
    icon: 'Star',
    emoji: '✨',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Fanciful (playful, one imaginative twist)
- Purpose: add personality without losing relevance to what's actually pictured.
- Voice: witty, light, grounded; one playful hook or personification max.
- Title: ≤ 48 chars; clever is fine, but keep subject identifiable.
- Description: ${generateSentenceStructure(sentenceCount, ['factual setup', 'whimsical turn using one flavor from [' + LEXICONS.fancifulFlavor.join(", ") + '] and one unexpected verb. Do not invent characters or events not implied by the image'])}
- Keep jokes dry; avoid baby-talk, hyperbole, or sarcasm toward people.`
  },

  humor: {
    key: 'humor',
    title: 'Humor & Punny',
    shortTitle: 'Humor',
    description: 'Witty with one clean joke',
    longDescription: 'Tight, clever descriptions with one well-placed photographic or compositional pun or joke. Keeps it light while staying informative.',
    bestFor: 'Pets, food, everyday moments',
    example: 'This dog is clearly having a ruff day in the park.',
    exampleTitle: 'Pier Pressure',
    exampleDescription: 'Wooden pier extends into calm waters on a sunny morning. Sometimes you just have to go with the flow.',
    gradient: 'from-orange-50 to-orange-100',
    selectedGradient: 'from-orange-500 to-orange-600',
    borderColor: 'border-orange-200',
    selectedBorderColor: 'border-orange-500',
    iconColor: 'text-orange-600',
    category: 'Creative',
    icon: 'Smile',
    emoji: '😄',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Humor/Punny (tight, one joke max)
- Purpose: add wit while keeping subject identifiable and searchable.
- Voice: breezy, contemporary; keep wordplay smart.
- Title: ≤ 48 chars; one clean pun that still clearly names the subject.
- Description: ${generateSentenceStructure(sentenceCount, ['literal context', 'punchline or wordplay callback (no more than 10 words). End with a period, not an exclamation'])}
- Tags: include subject-first, then add "pun", "humor" tags.
- Never mock people; steer toward situational humor.`
  },

  culturalReferences: {
    key: 'culturalReferences',
    title: 'Cultural References',
    shortTitle: 'Cultural References',
    description: 'References songs, movies, books & pop culture',
    longDescription: 'Uses famous songs, movies, TV shows, books, plays, and magazines as inspiration for mood-forward scenes. Factual descriptions that tie cultural references to the visual.',
    bestFor: 'Mood-forward scenes, night city, road trips, nostalgia',
    example: 'City street at night with neon reflections.',
    exampleTitle: 'Under the Bridge — Mission Creek',
    exampleDescription: 'Urban creek flows beneath concrete bridge structure, illuminated by streetlights reflecting off the water surface.',
    gradient: 'from-indigo-50 to-indigo-100',
    selectedGradient: 'from-indigo-500 to-indigo-600',
    borderColor: 'border-indigo-200',
    selectedBorderColor: 'border-indigo-500',
    iconColor: 'text-indigo-600',
    category: 'Creative',
    icon: 'Music',
    emoji: '🎵',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Cultural References (songs, movies, books, pop culture)
- Purpose: tie the image to a specific cultural touchstone to create mood-forward narration.
- Title: Use a distinctive title pulled from a real song, album, film, TV episode, book chapter, play, or magazine feature (≤ 55 chars); just the title, nothing else.
- Description: ${generateSentenceStructure(sentenceCount, ['literal description', 'explain how the reference matches the scene (medium, decade, or vibe) without quoting lyrics'])}
- Tags: include subject, place, plus cultural reference tags (artist, decade, genre, medium when relevant).
- Never create fictional works or mix multiple references in one caption.`
  },

  travelGuide: {
    key: 'travelGuide',
    title: 'Travel Guide / Local',
    shortTitle: 'Travel Guide',
    description: 'Informative like a guidebook',
    longDescription: 'Informative descriptions that explain what places are and why they\'re notable. Includes local context and practical details.',
    bestFor: 'Landmarks, neighborhoods, trips',
    example: 'Historic market square with vendors selling local produce.',
    exampleTitle: 'Kyoto — Alley near Pontocho',
    exampleDescription: 'Narrow traditional alley leading toward the famous Pontocho dining district. Stone lanterns mark the entrance to one of Kyoto\'s most atmospheric entertainment quarters.',
    gradient: 'from-teal-50 to-teal-100',
    selectedGradient: 'from-teal-500 to-teal-600',
    borderColor: 'border-teal-200',
    selectedBorderColor: 'border-teal-500',
    iconColor: 'text-teal-600',
    category: 'Professional',
    icon: 'MapPin',
    emoji: '🗺️',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Travel Guide/Local (informative guidebook style)
- Purpose: explain what places are and why they're notable, like a guidebook entry.
- Voice: confident local expert; informative but concise.
- Title: ≤ 55 chars. Pattern: <place>, <specific feature> or <landmark>.
- Description: ${generateSentenceStructure(sentenceCount, ['what/where with neighborhood or region', 'why it matters (history, use, vantage) without speculation'])}
- Tags: city, neighborhood, landmark, season, cultural context in lowercase order of specificity.
- Avoid travel clichés like "hidden gem" or "must-see".`
  },

  natureGuide: {
    key: 'natureGuide',
    title: 'Nature Field Guide',
    shortTitle: 'Nature Guide',
    description: 'Scientific and observational',
    longDescription: 'Detailed scientific descriptions focusing on observable characteristics, behaviors, and natural history details.',
    bestFor: 'Wildlife, plants, landscapes',
    example: 'Adult male cardinal perched on oak branch, displaying territorial behavior.',
    exampleTitle: 'Red-tailed Hawk (Buteo jamaicensis), soaring',
    exampleDescription: 'Adult red-tailed hawk demonstrates characteristic soaring behavior, wings spread wide to catch thermal currents. Light morph coloration visible on chest and distinctive tail banding apparent.',
    gradient: 'from-emerald-50 to-emerald-100',
    selectedGradient: 'from-emerald-500 to-emerald-600',
    borderColor: 'border-emerald-200',
    selectedBorderColor: 'border-emerald-500',
    iconColor: 'text-emerald-600',
    category: 'Professional',
    icon: 'TreePine',
    emoji: '🌿',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Nature Field Guide (scientific, observational)
- Purpose: describe wildlife, plants, landscapes with scientific accuracy.
- Voice: field biologist; precise nouns, zero flourish.
- Title: Common name (and scientific name if identifiable) + behavior/context.
- Description: ${generateSentenceStructure(sentenceCount, ['observable identification markers (size, color, morphology)', 'behavior or habitat note grounded in the frame'])}
- Tags: taxonomy, habitat, behavior terms ("foraging", "perched", "blooming").
- Never speculate beyond what the image confirms.`
  },

  technical: {
    key: 'technical',
    title: 'Technical / Photographer\'s Note',
    shortTitle: 'Technical',
    description: 'Focuses on photographic technique',
    longDescription: 'Emphasizes visible photographic techniques and technical aspects. Perfect for photography enthusiasts and educational content.',
    bestFor: 'Enthusiast audiences, teaching',
    example: 'Shallow depth of field isolates subject from background bokeh.',
    exampleTitle: 'Breakwater, 8-second exposure',
    exampleDescription: 'Long exposure technique smooths wave motion around rocky breakwater. Extended shutter time creates silk-like water texture while maintaining sharp detail in stationary rocks.',
    gradient: 'from-slate-50 to-slate-100',
    selectedGradient: 'from-slate-500 to-slate-600',
    borderColor: 'border-slate-200',
    selectedBorderColor: 'border-slate-500',
    iconColor: 'text-slate-600',
    category: 'Professional',
    icon: 'Camera',
    emoji: '📸',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Technical/Photographer's Note (technique-focused)
- Purpose: educate about visible photographic techniques and technical aspects.
- Voice: teaching photographer; precise verbs, confident explanations.
- Title: ≤ 50 chars. Subject + technique (e.g., "Waterfall, 2-second exposure").
- Description: ${generateSentenceStructure(sentenceCount, ['subject + visible method (lighting, lens effect, motion)', 'how that method shapes the result (texture, focus, mood). Avoid gear brands unless explicitly provided'])}
- Tags: technique-first ("long-exposure", "bokeh", "panning", "backlighting").`
  },

  journalistic: {
    key: 'journalistic',
    title: 'Journalistic',
    shortTitle: 'Journalistic',
    description: 'News caption style',
    longDescription: 'Professional news-style captions with who, what, where, when details. Objective and informative like newspaper photo credits.',
    bestFor: 'Events, street photography, documentary',
    example: 'Local residents gather at community center for weekly farmers market.',
    exampleTitle: 'Farmers\' market — Santa Cruz, Aug 2025',
    exampleDescription: 'Vendors arrange fresh produce at weekly farmers market in downtown Santa Cruz. Market operates Saturdays from 8am-1pm featuring local organic growers.',
    gradient: 'from-gray-50 to-gray-100',
    selectedGradient: 'from-gray-500 to-gray-600',
    borderColor: 'border-gray-200',
    selectedBorderColor: 'border-gray-500',
    iconColor: 'text-gray-600',
    category: 'Professional',
    icon: 'Newspaper',
    emoji: '📰',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Journalistic (news caption/credit style)
- Purpose: provide objective, news-style information like newspaper photo captions.
- Voice: wire service; no adjectives unless they relay verifiable conditions.
- Title: ≤ 55 chars. Pattern: <who/what> — <where>, <when> (if known).
- Description: ${generateSentenceStructure(sentenceCount, ['who/what/where/when', 'factual detail that adds context (action, numbers, turnout, weather) without opinion'])}
- Tags: subjects, location, event type, time-of-day context.
- Avoid verbs implying speculation ("seems", "appears").`
  },

  expressive: {
    key: 'expressive',
    title: 'Expressive',
    shortTitle: 'Expressive',
    description: 'Metaphorical and conceptual',
    longDescription: 'Creative, imaginative descriptions that use metaphor and metaphoric language to evoke emotion and convey meaning. Perfect for capturing the essence of a scene.',
    bestFor: 'Landscapes, abstract, conceptual compositions',
    example: 'The sun\'s rays dance across the horizon, painting the sky with a palette of fiery oranges and deep purples.',
    exampleTitle: 'Sunset Over the Mountains',
    exampleDescription: 'The sun\'s rays dance across the horizon, painting the sky with a palette of fiery oranges and deep purples.',
    gradient: 'from-violet-50 to-violet-100',
    selectedGradient: 'from-violet-500 to-violet-600',
    borderColor: 'border-violet-200',
    selectedBorderColor: 'border-violet-500',
    iconColor: 'text-violet-600',
    category: 'Creative',
    icon: 'Feather',
    emoji: '💭',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Expressive & Metaphorical (Conceptual, abstract, visually rich)
- Purpose: Create abstract, poetic interpretations that draw on emotion, mood, or transformation rather than literal description.
- Voice: Conceptual, metaphorical, sensory-rich. Contrast human perception with the physical world—a lens turned inward.
- Title Guidelines (2–5 words, Title Case):
  * Suggest mood or transformation, not what's literally shown
  * Combine sensory and emotional imagery (light, sound, color, memory, motion)
  * Avoid concrete locations or series names
  * Invite ambiguity that encourages interpretation
  * Example titles: "The Gravity of Silence", "When Color Sleeps", "Shadows Remember", "Dream Logic", "The Edge of Stillness"
- Description: ${generateSentenceStructure(sentenceCount, ['abstract interpretation that moves beyond literal observation', 'emotional or conceptual resonance using metaphor and sensory language'])}
- Tags: Include mood/concept tags ("memory", "transformation", "stillness") alongside visual elements, but prioritize emotional/thematic tags.
- Embrace ambiguity. Avoid literal descriptions and camera terminology.`
  },

  cinematic: {
    key: 'cinematic',
    title: 'Cinematic',
    shortTitle: 'Cinematic',
    description: 'Film-like scene descriptions',
    longDescription: 'Dramatic descriptions that read like film stills or movie scene notes. Emphasizes lighting, atmosphere, and visual drama.',
    bestFor: 'Dramatic light, landscapes, night scenes',
    example: 'Lone figure silhouetted against neon-lit cityscape at midnight.',
    exampleTitle: 'Crosswalk in rain, neon dusk',
    exampleDescription: 'Urban intersection transforms into a canvas of reflected neon as evening rain creates mirror surfaces on asphalt. Pedestrians move through pools of colored light.',
    gradient: 'from-red-50 to-red-100',
    selectedGradient: 'from-red-500 to-red-600',
    borderColor: 'border-red-200',
    selectedBorderColor: 'border-red-500',
    iconColor: 'text-red-600',
    category: 'Creative',
    icon: 'Film',
    emoji: '🎬',
    getPrompt: (sentenceCount = 3) => `${BASE_PROMPT}

Style: Cinematic (Dramatic, atmospheric, emotionally charged)
- Purpose: Create titles and descriptions that feel like stills from a film—balancing emotion, visual storytelling, and dramatic atmosphere.
- Voice: Cinematic and emotionally charged. Focus on light, contrast, and implied story rather than literal observation.
- Title Guidelines (3–6 words, Title Case):
  * Focus on light, contrast, and implied story
  * Evoke motion, anticipation, or an unfolding moment
  * Avoid place names, personal identifiers, or clichés
  * Use rhythm, pacing, or subtle tension in phrasing
  * Think of how a film might title a single frozen moment
  * Example titles: "Where the Light Breaks", "Midnight in Reverse", "The Weight of Air", "Through Glass and Shadow", "Everything Slows Down"
- Description: ${generateSentenceStructure(sentenceCount, ['dramatic visual setup emphasizing light, contrast, and atmosphere', 'emotional resonance or implied narrative using cinematic language'])}
- Tags: Include lighting terms ("backlit", "neon", "silhouette"), atmospheric mood, and implied motion or emotion; include "cinematic".
- Avoid camera settings, gear references, or literal descriptions of action.`
  },

  shuffle: {
    key: 'shuffle',
    title: 'Shuffle (Random Mix)',
    shortTitle: 'Shuffle',
    description: 'Random style for each photo',
    longDescription: 'Each photo gets a randomly selected style from all available options. Perfect for adding variety and discovering new ways to describe your photos.',
    bestFor: 'Large photo sets, experimentation, variety',
    example: 'Each photo will get a surprise style - could be artistic, humorous, technical, or any other!',
    exampleTitle: 'Style Surprise!',
    exampleDescription: 'This photo will receive one of 11 different AI writing styles chosen at random. Every photo in your batch will be unique and interesting.',
    gradient: 'from-purple-50 via-pink-50 to-orange-50',
    selectedGradient: 'from-purple-500 via-pink-500 to-orange-500',
    borderColor: 'border-purple-200',
    selectedBorderColor: 'border-purple-500',
    iconColor: 'text-purple-600',
    category: 'Special',
    special: true,
    icon: 'Shuffle',
    emoji: '🎲',
    getPrompt: (sentenceCount = 3) => {
      // Shuffle uses a random style's prompt, handled by the service
      return BASE_PROMPT;
    }
  }
};

// Helper functions
export const getStylesByCategory = () => {
  const categories = {};
  Object.values(AI_STYLES).forEach(style => {
    if (!categories[style.category]) {
      categories[style.category] = [];
    }
    categories[style.category].push(style);
  });
  return categories;
};

export const getStyleOptions = () => {
  return Object.values(AI_STYLES).map(style => ({
    value: style.key,
    label: `${style.shortTitle} - ${style.description}`
  }));
};

export const getStyle = (key) => {
  return AI_STYLES[key] || AI_STYLES.descriptive;
};

// Get prompt for a style (used by backend)
export const getPromptTemplate = (style, sentenceCount = 3) => {
  const styleConfig = AI_STYLES[style];
  if (!styleConfig || !styleConfig.getPrompt) {
    return AI_STYLES.descriptive.getPrompt(sentenceCount);
  }
  return styleConfig.getPrompt(sentenceCount);
};

// For backwards compatibility
export const STYLE_CATEGORIES = {
  Professional: ['descriptive', 'travelGuide', 'natureGuide', 'technical', 'journalistic'],
  Creative: ['artistic', 'fanciful', 'humor', 'culturalReferences', 'expressive', 'cinematic'],
  Special: ['shuffle']
};
